import type { StoryPhoto } from '../components/StoryGallery';

export type LegacyMilestone = {
  year: string;
  title: string;
  locations: string;
  description: string;
  photos: StoryPhoto[];
  note?: string;
};

export const legacyMilestones: LegacyMilestone[] = [

  {
    year: '2001',
    title: 'Where It All Began',
    locations: 'Damascus • Mecca & Medina • Cairo',
    description:
      'In 2001, a small group of students set out with a simple intention: to study Islam, language, and culture at their sources. The journey began in Damascus and established the foundation for years of learning, travel, and personal growth.',
    photos: [
      {
        url: '/media/legacy/2001-001.webp',
        title: 'Abu Nour Islamic Foundation',
        narrative:
          'At the Abu Nour Islamic Foundation in Damascus, students immersed themselves in classical Islamic studies, Arabic language, and daily life in one of the world’s oldest cities of learning.',
      },
      {
        url: '/media/legacy/2001-002.webp',
        title: 'Mecca and Medina',
        narrative:
          'From Damascus, the group traveled to the Hejaz, beginning in Mecca to perform ʿUmrah and continuing on to Medina. At the Prophet’s Mosque, students reflected on the spiritual and historical significance of the city and its role in Islamic civilization.',
      },
      {
        url: '/media/legacy/2001-003.webp',
        title: 'Cairo and Beyond',
        narrative:
          'In Egypt, study and travel unfolded across multiple regions. Students visited the pyramids before continuing north to Alexandria. The journey proceeded east through the Sinai Peninsula, where students ascended Mount Sinai before concluding in Sharm El Sheikh along the Red Sea.',
      },
    ],
  },
  {
    year: '2002',
    title: 'Faith, Scholarship, and Living History',
    locations: 'Syria • Saudi Arabia • Egypt',
    description:
      'In the second year, students returned to Damascus to deepen the foundation established in 2001. Through access to scholars, sacred spaces, and historic sites, the program emphasized continuity, lived experience, and historical understanding, while continuing travel to Egypt and the Hejaz.',
    photos: [
      {
        url: '/media/legacy/2002-001.webp',
        title: 'Khalid ibn al-Walid Mosque, Homs',
        narrative:
          'Students traveled to Homs to visit the Khalid ibn al-Walid Mosque, grounding their studies in the legacy of one of Islam’s most significant companions and early leaders.',
      },
      {
        url: '/media/legacy/2002-002.webp',
        title: 'Meeting with the Mufti of Syria',
        narrative:
          'Following Jumʿah prayer in Damascus, students met with the Mufti of Syria, Shaykh Ahmad Kuftaro, alongside the Honorable Minister Louis Farrakhan and distinguished guests, gaining direct exposure to Islamic scholarship and global Muslim leadership.',
      },
      {
        url: '/media/legacy/2002-003.webp',
        title: 'Umayyad Mosque, Damascus',
        narrative:
          'The Umayyad Mosque served as a living classroom, connecting students to one of the earliest and most enduring centers of Islamic worship and learning.',
      },
      {
        url: '/media/legacy/2002-004.webp',
        title: 'Castles of Salah al-Din',
        narrative:
          'Students visited historic fortresses reclaimed by Salah al-Din from the Crusaders, studying Islamic history through geography, architecture, and strategic context.',
      },
    ],
  },
  {
    year: '2003',
    title: 'Expansion to Southeast Asia',
    locations: 'Kuala Lumpur • Selangor • Taman Negara',
    description:
      'In 2003, the program expanded to Southeast Asia with study at the International Islamic University Malaysia, known as the Garden of Knowledge and Virtue. This new chapter preserved the spirit of Damascus while opening the door to learning within a different Muslim society and academic environment.',
    photos: [
      {
        url: '/media/legacy/2003-001.webp',
        title: 'International Islamic University Malaysia',
        narrative:
          'At the center of the IIUM campus, students gathered near the mosque and wudu area, grounding daily academic life in worship, reflection, and community.',
      },
      {
        url: '/media/legacy/2003-002.webp',
        title: 'Life in Kuala Lumpur',
        narrative:
          'Students navigated the streets of Kuala Lumpur together, adjusting to the pace, scale, and diversity of a major global city while building independence and group cohesion.',
      },
      {
        url: '/media/legacy/2003-003.webp',
        title: 'Sultan Salahuddin Abdul Aziz Mosque, Shah Alam',
        narrative:
          'A visit to the Shah Alam Mosque introduced students to Malaysia’s architectural expression of Islam and its role within contemporary civic life.',
      },
      {
        url: '/media/legacy/2003-004.webp',
        title: 'Journey to Taman Negara',
        narrative:
          'Students traveled two hours upstream into Taman Negara, one of the world’s oldest rainforests, encountering Malaysia’s natural environment alongside its urban centers.',
      },
      {
        url: '/media/legacy/2003-005.webp',
        title: 'Rainforest Trekking',
        narrative:
          'Jungle trekking and crossings over man-made suspension bridges emphasized resilience, teamwork, and engagement with the physical landscape.',
      },
      {
        url: '/media/legacy/2003-006.webp',
        title: 'Scholarly Continuity from Damascus',
        narrative:
          'Shaykh Osama Hakim, our beloved teacher from Damascus, joined the cohort in Malaysia, reinforcing continuity in instruction and linking earlier studies in Damascus with the Southeast Asian context.',
      },
      {
        url: '/media/legacy/2003-007.webp',
        title: 'IIUM Retreat and International Institute for Muslim Unity',
        narrative:
          'During a retreat with the International Institute for Muslim Unity, students engaged with Dr. Sano Koutoub Mustafa and IIUM scholars, connecting academic study with reflection, dialogue, and global Muslim thought.',
      },
    ],
  },
  {
    year: '2004',
    title: 'Deepening Academic Engagement',
    locations: 'Kuala Lumpur • Kuantan',
    description:
      'In 2004, the program returned to Malaysia with a stronger academic focus, integrating classroom study, professional exposure, and daily life within the International Islamic University Malaysia. As the program matured, each cohort benefited from clearer structure, deeper partnerships, and a growing sense of purpose.',
    photos: [
      {
        url: '/media/legacy/2004-001.webp',
        title: 'Arrival in Malaysia',
        narrative:
          'Students arrived at Kuala Lumpur International Airport and were welcomed by IIUM transportation, marking the beginning of another term of study and immersion.',
      },
      {
        url: '/media/legacy/2004-002.webp',
        title: 'IIUM Kuantan Medical Campus',
        narrative:
          'A visit to IIUM’s Kuantan medical campus introduced students to Islamic approaches to medicine, ethics, and professional education within a global academic setting.',
      },
      {
        url: '/media/legacy/2004-003.webp',
        title: 'Medical Studies and Inquiry',
        narrative:
          'During laboratory visits, students examined preserved human specimens, gaining exposure to medical science and research environments uncommon in traditional study abroad programs.',
      },
      {
        url: '/media/legacy/2004-004.webp',
        title: 'Everyday Life and Halal Access',
        narrative:
          'Students explored Malaysia’s diverse halal food options, appreciating the ease of daily life within a society structured around Muslim needs and practices.',
      },
      {
        url: '/media/legacy/2004-005.webp',
        title: 'Campus Life at IIUM',
        narrative:
          'Waiting at the main entrance of IIUM, students moved through campus routines that blended academic responsibility, community life, and spiritual rhythm.',
      },
    ],
  },
  {
    year: '2005',
    title: 'Service, Reflection, and Regional Reach',
    locations: 'Kuala Lumpur • Langkawi • Banda Aceh',
    description:
      'In 2005, the Malaysia program welcomed a new cohort at the International Islamic University Malaysia and expanded its regional reach through service and reflection in Banda Aceh, connecting study abroad with responsibility to the wider Muslim world.',
    photos: [
      {
        url: '/media/legacy/2005-001.webp',
        title: 'A New Cohort at IIUM',
        narrative:
          'At the familiar front entrance of IIUM, a new group of students arrived to continue the program’s growing tradition of study, community, and cultural immersion.',
      },
      {
        url: '/media/legacy/2005-002.webp',
        title: 'Banda Aceh After the Tsunami',
        narrative:
          'Students traveled to Banda Aceh and witnessed areas reshaped by the tsunami, gaining a grounded understanding of loss, recovery, and the long-term realities communities face after disaster.',
      },
      {
        url: '/media/legacy/2005-003.webp',
        title: 'Service with Orphans Under Baznas Care',
        narrative:
          'Students volunteered and brought aid to orphans under the care of Baznas, translating learning into service through direct support, presence, and compassion.',
      },
      {
        url: '/media/legacy/2005-004.webp',
        title: 'Ships Carried Inland',
        narrative:
          'The sight of ships pushed into the streets served as a lasting reminder of the tsunami’s force and the scale of what local residents endured.',
      },
      {
        url: '/media/legacy/2005-005.webp',
        title: 'Langkawi Cable Car',
        narrative:
          'In Langkawi, students traveled by cable car to the mountain summit, experiencing Malaysia’s natural beauty while strengthening bonds within the cohort.',
      },
      {
        url: '/media/legacy/2005-006.webp',
        title: 'Langkawi Suspension Bridge',
        narrative:
          'Crossing the suspension bridge in Langkawi, students embraced challenge and perspective, reinforcing resilience, trust, and shared experience.',
      },
    ],
  },
  {
    year: '2006',
    title: 'Regional Expansion and Deeper Partnerships',
    locations: 'Malaysia • Thailand',
    description:
      'In 2006, a new cohort returned to the Garden of Knowledge and Virtue at IIUM, strengthening the program’s rhythm of study, orientation, and spiritual growth. Alongside landmark visits in Malaysia, the journey widened into Southern Thailand, where students engaged a sister institution and experienced Islamic learning in a new regional context.',
    photos: [
      {
        url: '/media/legacy/2006-001.webp',
        title: 'A New Cohort at IIUM',
        narrative:
          'Students gathered on the entrance steps of IIUM, marking the arrival of a new group and the continuation of a growing tradition of study and travel.',
      },
      {
        url: '/media/legacy/2006-002.webp',
        title: 'Orientation Welcome with Dr. Abdul Aziz Berghout',
        narrative:
          'Dr. Abdul Aziz Berghout welcomed the students and set the academic tone through orientation, grounding the cohort in purpose, expectations, and a shared standard of conduct.',
      },
      {
        url: '/media/legacy/2006-003.webp',
        title: 'Yala Islamic College, Pattani Campus',
        narrative:
          'In Southern Thailand, students attended an academic session at Yala Islamic College, connecting with scholars and students while observing the region’s living Islamic heritage.',
      },
    ],
  },
  {
    year: '2007',
    title: 'Parallel Paths of Learning',
    locations: 'Malaysia • Thailand • United Arab Emirates',
    description:
      'By 2007, the program had matured into a multi-site initiative. Cohorts studied in Malaysia and Thailand while a parallel program unfolded in the United Arab Emirates, reflecting a growing footprint across Southeast Asia and the Gulf.',
    photos: [
      {
        url: '/media/legacy/2007-001.webp',
        title: 'Langkawi Suspension Bridge',
        narrative:
          'Students explored Langkawi’s natural landscape, blending reflection and community during a walk across the island’s suspension bridge.',
      },
      {
        url: '/media/legacy/2007-002.webp',
        title: 'Pantai Cenang, Langkawi',
        narrative:
          'Time at Pantai Cenang offered space for rest and informal dialogue alongside structured academic travel.',
      },
      {
        url: '/media/legacy/2007-003.webp',
        title: 'Langkawi Archipelago',
        narrative:
          'A boat journey through the Langkawi archipelago introduced students to the region’s maritime geography and heritage.',
      },
      {
        url: '/media/legacy/2007-004.webp',
        title: 'Bangkok Transit',
        narrative:
          'Travel through Bangkok exposed students to the scale and diversity of urban life in Southeast Asia.',
      },
      {
        url: '/media/legacy/2007-005.webp',
        title: 'UAE University Campus',
        narrative:
          'A second cohort studied at United Arab Emirates University in Al-Ain, marking the program’s expansion into the Gulf region.',
      },
      {
        url: '/media/legacy/2007-006.webp',
        title: 'Conference Participation',
        narrative:
          'Students gathered after an academic conference, engaging with scholars and peers in a professional setting.',
      },
      {
        url: '/media/legacy/2007-007.webp',
        title: 'Student Housing, Al-Ain',
        narrative:
          'All students resided in on-campus housing, fully immersed in daily academic and campus life.',
      },
      {
        url: '/media/legacy/2007-008.webp',
        title: 'Museum Visit',
        narrative:
          'Museum visits complemented coursework by grounding regional history in material culture.',
      },
      {
        url: '/media/legacy/2007-009.webp',
        title: 'Jumeirah Mosque, Dubai',
        narrative:
          'A visit to Jumeirah Mosque introduced students to Islamic architecture and public religious life in Dubai.',
      },
    ],
  },
  {
    year: '2008',
    title: 'Academic Continuity and Regional Exposure',
    locations: 'Kuala Lumpur • Putrajaya • Langkawi • Singapore',
    description:
      'The 2008 program centered on academic study at IIUM in Kuala Lumpur, with regional visits to Putrajaya, Langkawi, and Singapore. These experiences broadened student perspective through institutional access, civic learning, and cross-cultural engagement in Southeast Asia.',
    photos: [
      {
        url: '/media/legacy/2008-001.webp',
        title: 'Putrajaya Academic Visit',
        narrative:
          'Former MSA president Omar supported student participation through a scholarship initiative that enabled academic visits to Putrajaya.',
      },
      {
        url: '/media/legacy/2008-002.webp',
        title: 'Putrajaya Waterways',
        narrative:
          'Students explored Putrajaya by boat, observing the city’s planned civic and institutional design.',
      },
      {
        url: '/media/legacy/2008-003.webp',
        title: 'Putrajaya Mosque',
        narrative:
          'A visit to the Putrajaya Mosque highlighted Malaysia’s contemporary Islamic architecture.',
      },
      {
        url: '/media/legacy/2008-004.webp',
        title: 'Sultan Mosque Visit',
        narrative:
          'Students visited Sultan Mosque in Singapore as part of a broader study of Islam in Southeast Asia.',
      },
      {
        url: '/media/legacy/2008-005.webp',
        title: 'Inside Sultan Mosque',
        narrative:
          'An interior visit to Sultan Mosque emphasized its role as a religious and cultural landmark.',
      },
      {
        url: '/media/legacy/2008-006.webp',
        title: 'Ministry of Culture Visit',
        narrative:
          'A visit to Singapore’s Ministry of Culture provided insight into national identity and cultural policy.',
      },
      {
        url: '/media/legacy/2008-007.webp',
        title: 'Traditional Malay Stilt Houses',
        narrative:
          'Students observed traditional Malay stilt houses (Rumah Panggung) built above water in Langkawi.',
      },
    ],
  },
  {
    year: '2009',
    title: 'Applied Learning and Community',
    locations: 'Kuala Lumpur • Kuantan',
    description:
      'In 2009, the program emphasized applied learning through continued study at IIUM, with focused academic workshops in Kuantan centered on biomedical and medical sciences. Alongside classroom work, students strengthened community through shared experiences and reflection.',
    photos: [
      {
        url: '/media/legacy/2009-001.webp',
        title: 'Traditional Batik Workshop',
        narrative:
          'In Kuantan, students learned and created traditional batik artwork as part of cultural immersion.',
      },
      {
        url: '/media/legacy/2009-002.webp',
        title: 'Sungai Pandan Waterfall',
        narrative:
          'The group visited Sungai Pandan Waterfall, combining reflection with time in nature.',
      },
      {
        url: '/media/legacy/2009-003.webp',
        title: 'Program Reflection Lunch',
        narrative:
          'Students gathered for a final group lunch to reflect on their academic and personal growth.',
      },
    ],
  },
  {
    year: '2010',
    title: 'Continuity, Scholarship, and Return',
    locations: 'Malaysia • Singapore • Damascus, Syria',
    description:
      'In 2010, the program unfolded across two distinct seasons. During the summer, students studied and traveled throughout Malaysia, deepening academic engagement while exploring the country’s cultural and natural landscapes. In the winter, the journey returned to Damascus, reconnecting students to a foundational place of learning and creating a full-circle moment that linked the program’s earliest years with its continued growth.',
    photos: [
      {
        url: '/media/legacy/2010-001.webp',
        title: 'Yayasan Restu Quran Centre',
        narrative:
          'Students visited the Yayasan Restu Quran Centre, a renowned institution dedicated to Quranic preservation, calligraphy, and manuscript arts, gaining insight into Malaysia’s contributions to Islamic scholarship.',
      },
      {
        url: '/media/legacy/2010-002.webp',
        title: 'Putrajaya: Governance and Sacred Space',
        narrative:
          'In Putrajaya, students visited the Prime Minister’s Office at Perdana Putra and the Putra Mosque, exploring the relationship between governance, architecture, and religious life in modern Malaysia.',
      },
      {
        url: '/media/legacy/2010-003.webp',
        title: 'Putrajaya by Water',
        narrative:
          'A boat ride through Putrajaya offered a different perspective on the city’s urban planning and symbolic design.',
      },
      {
        url: '/media/legacy/2010-004.webp',
        title: 'Cameron Highlands Tea Plantations',
        narrative:
          'Students traveled to the Cameron Highlands to visit Malaysia’s iconic tea plantations, learning about the region’s agricultural history and environment.',
      },
      {
        url: '/media/legacy/2010-005.webp',
        title: 'White Water Rafting in Gopeng',
        narrative:
          'Outdoor activities in Gopeng provided students with an opportunity for teamwork, challenge, and reflection beyond the classroom.',
      },
      {
        url: '/media/legacy/2010-006.webp',
        title: 'Learning About Palestine',
        narrative:
          'At the Islamic Arts Museum Malaysia, students engaged with exhibits focused on Palestinian history and culture, connecting global realities to historical context.',
      },
      {
        url: '/media/legacy/2010-007.webp',
        title: 'Parent Information Session in Singapore',
        narrative:
          'In Singapore, parents attended an information session ahead of the winter program, reflecting community trust and cross-regional engagement.',
      },
      {
        url: '/media/legacy/2010-008.webp',
        title: 'Departure to Damascus',
        narrative:
          'The Mufti of Singapore, Ustaz Dr. Muhammad Fatris Bakaram, sent the student group off from Changi Airport as they departed for Damascus.',
      },
      {
        url: '/media/legacy/2010-009.webp',
        title: 'Arrival at Abu Nour Islamic Foundation',
        narrative:
          'Students arrived in Damascus at the Abu Nour Islamic Foundation, now known as the Shaykh Ahmed Kuftaro Foundation, reconnecting with a cornerstone of earlier programs.',
      },
      {
        url: '/media/legacy/2010-010.webp',
        title: 'Lecture with Dr. Ala Deen',
        narrative:
          'Following a formal lecture, students gathered for a group photo, marking an important moment of scholarly engagement.',
      },
      {
        url: '/media/legacy/2010-011.webp',
        title: 'Roman Theatre of Bosra',
        narrative:
          'Students visited the UNESCO World Heritage site of the Roman Theatre of Bosra, witnessing the scale and preservation of one of the ancient world’s most remarkable structures.',
      },
      {
        url: '/media/legacy/2010-012.webp',
        title: 'Bosra: Ground-Level View',
        narrative:
          'From the lower tiers of the theatre, students experienced the monument from within, grounding history in lived space.',
      },
      {
        url: '/media/legacy/2010-013.webp',
        title: 'Graduation Ceremony (Boys)',
        narrative:
          'Male students gathered for a graduation ceremony, marking the completion of their studies.',
      },
      {
        url: '/media/legacy/2010-014.webp',
        title: 'Graduation Ceremony (Girls)',
        narrative:
          'Female students marked their graduation in a parallel ceremony, reflecting the program’s structured academic conclusion.',
      },
      {
        url: '/media/legacy/2010-015.webp',
        title: 'Reunion with Shaykh Osama Hakim',
        narrative:
          'A reunion with Shaykh Osama Hakim connected past and present. Having led the program in 2001 and 2002 and participated again in 2003, this moment in 2010 symbolized continuity, mentorship, and enduring bonds.',
      },
    ],
  },
  {
    year: '2026',
    title: 'Continuing the Journey',
    locations: 'Kuala Lumpur • Banda Aceh • Doha',
    description:
      'In 2026, the program continues with a Summer Malaysia–Indonesia Academic Immersion hosted at the International Islamic University Malaysia, with an academic visit to Universitas Syiah Kuala in Banda Aceh, Indonesia. As part of the travel routing, students will pass through Doha, Qatar, allowing for a short academic familiarization visit to Education City. The visit will help students understand how Education City functions as a higher education ecosystem, including exposure to academic programs, research environments, and student support structures, so students gain clarity on how degree study in Doha is organized and experienced in practice.',
    photos: [
      {
        url: '/media/legacy/2026-001.webp',
        title: 'Looking Ahead',
        narrative:
          'The journey continues into a new chapter, connecting Southeast Asia and the Middle East through academic immersion, institutional engagement, and forward-looking pathways for future study.',
      },
    ],
    note:
      'The journey continues, grounded in scholarship, shaped by experience, and oriented toward the future.',
  },
];
